import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  PORTAL_PRESENCE_HEARTBEAT_MS,
  upsertPortalPresence,
  type PortalPresencePortal,
} from '../lib/portalPresence';

export function usePortalPresenceHeartbeat(portal: PortalPresencePortal) {
  const { authUser } = useAuth();
  const location = useLocation();
  const portalRef = useRef(portal);
  portalRef.current = portal;

  useEffect(() => {
    if (!authUser) return undefined;

    let cancelled = false;

    const ping = () => {
      if (cancelled) return;
      void upsertPortalPresence(portalRef.current).catch(() => {
        /* ignore transient network errors */
      });
    };

    ping();
    const intervalId = window.setInterval(ping, PORTAL_PRESENCE_HEARTBEAT_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') ping();
    };
    const onFocus = () => ping();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [authUser?.id, portal, location.pathname]);
}
